import { PROPOSAL_ACCENT, PROPOSAL_BG, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import {
  PDF_A4_HEIGHT_PX,
  PDF_A4_WIDTH_PX,
  PDF_QUOTATION_MARGIN_X_PX,
  PDF_QUOTATION_MARGIN_Y_PX,
} from "@/lib/proposalPdfQuotationLayout";
import type { DtoolsPdfPackageCover } from "@shared/dtoolsPdfPackage";

type Props = {
  cover: DtoolsPdfPackageCover;
};

/**
 * Cover with hero: subtle navy fade for text contrast (html2canvas-safe rgba).
 * Cover without hero: cream typography cover matching Link B ProposalCustomerHero — no gradient.
 *
 * Earlier bug: COVER_GRADIENT was always applied, so no-image covers rendered as a grey-to-black wash.
 */
const HERO_TEXT_OVERLAY =
  "linear-gradient(to top, rgba(11, 18, 32, 0.72), rgba(11, 18, 32, 0.22), rgba(11, 18, 32, 0))";

export default function DtoolsPdfPackageCoverPage({ cover }: Props) {
  const heroUrl = cover.heroImageUrl?.trim() || "";
  const hasHero = Boolean(heroUrl);

  const metaBlock = (
    <div
      className="ml-auto max-w-xl space-y-3 text-right"
      data-testid="dtools-pdf-cover-customer-block"
      style={{ color: hasHero ? "#ffffff" : PROPOSAL_TEXT }}
    >
      <p
        className="text-[11px] tracking-wide"
        style={{ fontWeight: 300, color: hasHero ? "rgba(255,255,255,0.92)" : `${PROPOSAL_TEXT}B3` }}
      >
        This proposal is prepared for
      </p>
      <h1 className="text-[28px] leading-tight" style={{ fontWeight: 300 }}>
        {cover.customerName}
      </h1>
      <p className="text-[18px] leading-snug" style={{ fontWeight: 300 }}>
        {cover.proposalTitle}
      </p>
      <p
        className="text-[11px] tracking-[0.18em]"
        style={{ fontWeight: 600, color: hasHero ? "#D4B87A" : PROPOSAL_ACCENT }}
      >
        {cover.proposalRef}
      </p>
      <div className="space-y-0.5 pt-1 text-[10px]" style={{ opacity: hasHero ? 0.95 : 0.85 }}>
        <p>Revision {cover.revisionNumber}</p>
        <p>{cover.proposalDate}</p>
      </div>
    </div>
  );

  return (
    <section
      className="pdf-page-break-after pdf-keep-together relative overflow-hidden"
      data-testid="dtools-pdf-cover-page"
      data-pdf-cover-mode={hasHero ? "hero" : "plain"}
      style={{
        fontFamily: PROPOSAL_COPY_FONT,
        color: PROPOSAL_TEXT,
        boxSizing: "border-box",
        width: `${PDF_A4_WIDTH_PX}px`,
        height: `${PDF_A4_HEIGHT_PX}px`,
        minHeight: `${PDF_A4_HEIGHT_PX}px`,
        margin: 0,
        background: PROPOSAL_BG,
      }}
    >
      {hasHero ? (
        <>
          <img
            src={heroUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="dtools-pdf-cover-hero"
            crossOrigin="anonymous"
          />
          <div
            className="relative flex h-full flex-col justify-end"
            data-testid="dtools-pdf-cover-content"
            style={{
              backgroundImage: HERO_TEXT_OVERLAY,
              padding: `${PDF_QUOTATION_MARGIN_Y_PX}px ${PDF_QUOTATION_MARGIN_X_PX}px`,
              boxSizing: "border-box",
            }}
          >
            {metaBlock}
          </div>
        </>
      ) : (
        <div
          className="relative flex h-full flex-col justify-center"
          data-testid="dtools-pdf-cover-content"
          style={{
            padding: `${PDF_QUOTATION_MARGIN_Y_PX}px ${PDF_QUOTATION_MARGIN_X_PX}px`,
            boxSizing: "border-box",
            background: PROPOSAL_BG,
          }}
        >
          {metaBlock}
        </div>
      )}
    </section>
  );
}
